import { Capacitor, registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import { paths } from './paths';
import { removeValue, writeValue } from './rtdb';
import { requireUid } from './auth';

/**
 * L'appel annonce au systeme.
 *
 * Une notification ordinaire ne fait pas sonner un telephone comme un
 * appel : pas de bouton pour decrocher, pas de vibration en rythme, rien
 * par-dessus l'ecran verrouille. C'est CallKit qui donne cela, et
 * PushKit qui reveille l'application pour le lui dire.
 *
 * Ce module est la couture cote page : il range le jeton VoIP, et il
 * ecoute ce que le systeme decide — decrocher, raccrocher. La sonnerie
 * et l'interface vivent dans `AppelSysteme.swift` ; la signalisation
 * reste ici, ou elle a toujours ete.
 *
 * **Deux jetons, deux branches.** Celui des notifications ordinaires
 * (`deviceTokens`) et celui des appels (`voipTokens`) sont delivres par
 * deux services differents et ne s'echangent pas. Les confondre ne
 * produirait aucune erreur, seulement un silence.
 */

interface Plugin {
  jeton(): Promise<{ jeton: string }>;
  interdit(): Promise<{ interdit: boolean }>;
  /** Un appel accepte au systeme que la page n'a pas encore appris. */
  enAttente(): Promise<{ callId: string }>;
  quiSonne(): Promise<{ callId: string }>;
  accepter(options: { callId: string }): Promise<void>;
  refuser(options: { callId: string }): Promise<void>;
  demarrer(options: { qui: string; callId: string; video: boolean }): Promise<{ ouvert: boolean }>;
  connecte(): Promise<void>;
  terminer(): Promise<void>;
  addListener(
    evenement: 'repondre' | 'raccrocher' | 'jetonVoip' | 'sonne',
    ecouteur: (donnees: { callId?: string; jeton?: string }) => void,
  ): Promise<PluginListenerHandle>;
}

const AppelSysteme = registerPlugin<Plugin>('AppelSysteme');

function natif(): boolean {
  /*
    iOS seulement pour l'instant : PushKit et CallKit n'existent que la.
    L'equivalent Android — un message FCM haute priorite et une
    notification plein ecran — aura son propre greffon Kotlin sous le
    meme nom ; ce jour-la, la garde s'elargira.
  */
  return Capacitor.getPlatform() === 'ios';
}

/** Le jeton VoIP en cours, pour savoir lequel oublier a la deconnexion. */
let jetonCourant: string | null = null;

/**
 * CallKit est-il interdit sur cet appareil ?
 *
 * Le ministere chinois de l'industrie a demande a Apple que CallKit soit
 * desactive dans toutes les applications distribuees sur l'App Store
 * chinois. La voix sur IP reste autorisee : c'est l'habillage du systeme
 * qui ne l'est pas. Le greffon tranche — boutique du compte et region de
 * l'appareil — et la page en tire deux consequences : aucun jeton VoIP a
 * ranger, et aucune annonce du systeme a attendre.
 *
 * Faux tant qu'on n'a pas demande : c'est le cas de l'immense majorite,
 * et l'attente de la banniere tombe d'elle-meme au bout de trois secondes.
 */
let interdit = false;

/**
 * Range le jeton VoIP de cet appareil.
 *
 * Meme prudence que pour les notifications ordinaires : PushKit repond
 * quand il veut, donc on ecoute **avant** de demander, et on redemande
 * quelques fois. Un jeton absent ne casse rien — l'appel arrivera par la
 * banniere, faute de mieux.
 */
export async function enregistrerLesAppels(): Promise<void> {
  if (!natif()) return;

  interdit = await AppelSysteme.interdit()
    .then((r) => Boolean(r?.interdit))
    .catch(() => false);
  /*
    Sans jeton VoIP, `sonnerLAppel` reprend la main et fait sonner par une
    notification ordinaire — comme avant que CallKit existe. C'est
    exactement ce qu'Apple laisse faire la-bas.
  */
  if (interdit) return;

  void AppelSysteme.addListener('jetonVoip', (donnees) => {
    void poser(donnees.jeton);
  });

  for (let essai = 0; essai < 5; essai += 1) {
    try {
      const { jeton } = await AppelSysteme.jeton();
      if (jeton) return poser(jeton);
    } catch {
      /*
        Le registre n'a pas encore repondu — ou le greffon natif n'est pas
        enregistre, ce qui fut le cas et ne se voyait nulle part. Les
        greffons de ce projet se declarent **a la main** dans
        `AppDelegate.swift` : compiler ne suffit pas a rendre le Swift
        atteignable.
      */
    }
    await new Promise((suite) => setTimeout(suite, 1000));
  }
}

async function poser(jeton: string | undefined): Promise<void> {
  if (!jeton || jeton === jetonCourant) return;
  jetonCourant = jeton;
  await writeValue(paths.voipToken(requireUid(), jeton), {
    platform: 'ios',
    updatedAt: Date.now(),
  }).catch(() => undefined);
}

/** Oublie cet appareil pour les appels. A la deconnexion. */
export async function oublierLesAppels(): Promise<void> {
  if (!natif() || !jetonCourant) return;
  const jeton = jetonCourant;
  jetonCourant = null;
  await removeValue(paths.voipToken(requireUid(), jeton)).catch(() => undefined);
}

/**
 * Ce que le systeme decide, rendu a l'application.
 *
 * `repondre` part quand le membre appuie sur le bouton vert de l'ecran
 * d'appel ; `raccrocher` sur le rouge. C'est l'ecran des appels qui
 * s'abonne : lui seul sait quoi en faire.
 */
export function suivreLAppelSysteme(handlers: {
  onRepondre: (callId: string) => void;
  onRaccrocher: (callId: string) => void;
  /** Le systeme vient d'annoncer cet appel : la page peut se montrer. */
  onSonne: (callId: string) => void;
}): () => void {
  if (!natif()) return () => undefined;

  const attentes = [
    AppelSysteme.addListener('repondre', (donnees) => {
      if (donnees.callId) handlers.onRepondre(donnees.callId);
    }),
    AppelSysteme.addListener('raccrocher', (donnees) => {
      if (donnees.callId) handlers.onRaccrocher(donnees.callId);
    }),
    AppelSysteme.addListener('sonne', (donnees) => {
      if (donnees.callId) handlers.onSonne(donnees.callId);
    }),
  ];

  /*
    Le rattrapage.

    Le bouton vert peut avoir ete presse avant que cette page existe :
    c'est meme le cas le plus courant, puisque l'appel reveille
    l'application. L'evenement est alors parti dans le vide, et sans cette
    question le membre se retrouve devant l'ecran qui sonne, invite a
    accepter ce qu'il vient d'accepter.
  */
  void AppelSysteme.enAttente()
    .then(({ callId }) => {
      if (callId) handlers.onRepondre(callId);
    })
    .catch(() => undefined);

  return () => {
    for (const attente of attentes) void attente.then((h) => h.remove()).catch(() => undefined);
  };
}

/**
 * Annonce au systeme l'appel qu'on passe.
 *
 * Jusqu'ici le systeme ne connaissait que les appels recus. Un appel
 * sortant n'existait que dans l'application : rien en haut de l'ecran,
 * rien sur l'ecran verrouille, rien dans les recents, et quitter Sirocco
 * pendant que ca sonnait ne laissait aucune trace d'un appel en cours.
 *
 * Un refus ne casse rien — le telephone peut etre deja pris par un vrai
 * appel — et l'appel se poursuit alors dans l'application seule.
 */
export async function ouvrirLAppelSysteme(
  qui: string,
  callId: string,
  video: boolean,
): Promise<void> {
  if (!natif()) return;
  await AppelSysteme.demarrer({ qui, callId, video }).catch(() => undefined);
}

/** L'autre a decroche : le systeme troque « appel en cours » pour le chronometre. */
export async function connecterLAppelSysteme(): Promise<void> {
  if (!natif()) return;
  await AppelSysteme.connecte().catch(() => undefined);
}

/**
 * Le systeme et l'application doivent sonner ensemble.
 *
 * La page lit la base en direct ; l'envoi VoIP, lui, fait le tour par
 * Apple. La banniere de l'application arrivait donc une a deux secondes
 * avant celle du systeme — et decrocher dans cet intervalle laissait
 * CallKit sonner par-dessus une conversation commencee. Son anneau
 * interrompt la session audio : on n'entendait plus rien jusqu'a ce qu'on
 * accepte aussi la banniere du haut.
 *
 * La page attend donc que le systeme ait annonce le meme appel. Rend
 * `true` la ou cette attente a un sens, c'est-a-dire la ou CallKit
 * existe.
 */
export function appelSystemeAttendu(): boolean {
  return natif() && !interdit;
}

/** L'appel que le systeme fait sonner maintenant, pour la page qui arrive apres. */
export async function appelSystemeQuiSonne(): Promise<string> {
  if (!natif()) return '';
  const reponse = await AppelSysteme.quiSonne().catch(() => null);
  return reponse?.callId ?? '';
}

/**
 * L'appel a ete pris dans l'application : le systeme suit.
 *
 * Il repond, il ne raccroche pas — l'appel systeme devient la
 * conversation et garde la session audio. Raccrocher la rendrait, et le
 * son tomberait.
 */
export async function accepterLAppelSysteme(callId: string): Promise<void> {
  if (!natif()) return;
  await AppelSysteme.accepter({ callId }).catch(() => undefined);
}

/** L'appel a ete refuse dans l'application : le systeme suit. */
export async function refuserLAppelSysteme(callId: string): Promise<void> {
  if (!natif()) return;
  await AppelSysteme.refuser({ callId }).catch(() => undefined);
}

/** Raccroche l'appel systeme quand la page en a fini avec lui. */
export async function fermerLAppelSysteme(): Promise<void> {
  if (!natif()) return;
  await AppelSysteme.terminer().catch(() => undefined);
}
